import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Cookies from "js-cookie";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import Title from "@/components/molecules/Title";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import {
  CarNoopReasonedEventsConstants,
  PAGE_SIZE,
  SavedState,
} from "./CarNoopReasonedEventsConstants";
import { Office, ONLY_CONTRACT } from "@/models/office";
import { getOfficesAPI } from "@/util/api/API";
import { MULTI_OFFICE_ID } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";
import { EvCarNoopEvents, SearchEvCarNoopEventsCondition } from "@/models/carUnderuseEvents";
import { countCarNoopReasonedEventsAPI, getCarNoopReasonedEventsAPI } from "@/util/api/carUnderuseEvents";
import { CarNoopReasonedEventsList } from "./CarNoopReasonedEventsList";
import { Typography } from "@mui/material";

export const CarNoopReasonedEventsPage = () => {
  const [searchCondition, setSearchCondition] =
    React.useState<SearchEvCarNoopEventsCondition>({
      officeIds: [],
    });

  const [total, setTotal] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(0);
  const [events, setEvents] = React.useState<EvCarNoopEvents[]>([]);

  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officesSelected, setOfficesSelected] = React.useState<Office[]>([]);

  const [noSearchResult, setNoSearchResult] = React.useState<boolean>(false);
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);

  const { state }: { state?: SavedState } = useLocation();
  const navigate = useNavigate();

  const handleReturn = React.useCallback(() => {
    navigate("/car_noop_events", { state });
  }, [navigate, state]);

  // 指定された条件で未稼働理由登録済みイベントを取得・更新する
  const updateEvents = React.useCallback(
    (condition: SearchEvCarNoopEventsCondition, page: number) => {
      getCarNoopReasonedEventsAPI(condition, page - 1, PAGE_SIZE).then((res) => {
        setEvents(res.data);
        setPage(page);
      });

      countCarNoopReasonedEventsAPI(condition).then((res) => {
        const count = res.data?.count ?? 0;
        setTotal(count);
        setNoSearchResult(count === 0);
      });
    },
    []
  );

  const handleChangeOffices = React.useCallback(
    (offices: Office[]) => {
      setOfficesSelected(offices);
      setSearchCondition({
        ...searchCondition,
        officeIds: offices?.map(({ id }) => id) ?? [],
      });
      Cookies.set(MULTI_OFFICE_ID, offices.map(({ id }) => id).join(","));
    },
    [searchCondition]
  );

  const handleOnPageChange = React.useCallback(
    (page: number) => {
      updateEvents(searchCondition, page);
    },
    [searchCondition, updateEvents]
  );

  // 検索条件に基づいてイベント一覧をフィルタリングする
  const handleOnClickSearch = React.useCallback(() => {
    updateEvents(searchCondition, 1);
  }, [searchCondition, updateEvents]);

  // 検索条件をクリアする
  const handleOnClickReset = React.useCallback(() => {
    setOfficesSelected([]);
    setSearchCondition({ officeIds: [] });
  }, []);

  const getOffices = React.useCallback(async () => {
    try {
      const res = await getOfficesAPI(ONLY_CONTRACT);
      setOffices(res.data || []);
      const founds = res.data.filter((d) =>
        Cookies.get(MULTI_OFFICE_ID)?.split(",").includes(d.id.toString())
      );
      if (founds.length) {
        setOfficesSelected(founds);
        setSearchCondition({
          ...searchCondition,
          officeIds: founds.map(({ id }) => id),
        });
      }

      setInitSearchFlag(true);
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  }, [searchCondition]);

  React.useEffect(() => {
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!initSearchFlag) {
      return;
    }

    // 保存された検索条件があれば、その検索条件をもとに車両一覧を取得する
    if (state != null && state.officesSelected != null) {
      setOfficesSelected(state.officesSelected);
      setPage(state.page);
      setSearchCondition(state.searchCondition);
      updateEvents(state.searchCondition, state.page);
    } else {
      handleOnClickSearch();
    }

    setInitSearchFlag(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initSearchFlag, state]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2, mb: 2, alignItems: "center" }}>
          <Title style={{ flexGrow: 1 }}>{CarNoopReasonedEventsConstants.CarNoopReasonedEvents}</Title>
          <CommonButton
            btnType="Return"
            onClickBtn={handleReturn}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item xs={6}>
                  <AutoCompleteMultiple
                    options={offices}
                    limit={100}
                    forSearch
                    getOptionLabel={getOfficeOptionLabel}
                    value={officesSelected}
                    onChange={handleChangeOffices}
                    label={CarNoopReasonedEventsConstants.CompanyOffice}
                  />
                </Grid>
                <Grid item xs={12} xl={2.7} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleOnClickSearch}
                  />
                  <CommonButton
                    sx={{ ml: 2 }}
                    btnType="ResetSearchCriteria"
                    onClickBtn={handleOnClickReset}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {!noSearchResult && (
            <Grid container direction="column">
              <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
                ※登録後に内容変更ご希望の場合は、gx_contact@goinc.jp までご連絡ください
              </Typography>
            </Grid>
          )}
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <CarNoopReasonedEventsList
                onChangePage={handleOnPageChange}
                events={events}
                pagingData={{ page, total, size: PAGE_SIZE }}
              />
              {noSearchResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
